import { z } from 'zod';
// Produk yang dituju desain adalah bentuk milik kontrak Studio (KEPUTUSAN 2B.4).
import { designTargetShape } from '@imaji-studio/contract';
import type { OrderStatus } from '../constants/index.js';
import { slugSchema } from './catalog.js';
import { idSchema } from './common.js';

/**
 * Berkas desain pelanggan. Tipe ditentukan dari ekstensi, bukan dari MIME
 * browser: AI dan CDR sering terbaca application/octet-stream atau kosong.
 * Nilai di sini adalah Content-Type yang ditandatangani server, jadi klien
 * wajib mengirim persis nilai ini saat PUT.
 */
export const DESIGN_FILE_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  ai: 'application/postscript',
  cdr: 'application/vnd.corel-draw',
} as const;

export type DesignFileExt = keyof typeof DESIGN_FILE_TYPES;

export const DESIGN_MAX_BYTES = 50 * 1024 * 1024;

/** Berkas yang tidak pernah menjadi bagian pesanan dihapus setelah sekian hari. */
export const DESIGN_ORPHAN_TTL_DAYS = 30;

/**
 * Status pesanan yang masih menerima (atau mengganti) desain dari pelanggan.
 * Setelah masuk produksi, perubahan desain harus lewat tim CI.
 */
export const DESIGN_EDITABLE_STATUSES: readonly OrderStatus[] = [
  'PENDING_PAYMENT',
  'PAID',
  'IN_REVIEW',
];

/**
 * Status pesanan yang masih boleh disetujui atau dibatalkan persetujuan
 * desainnya oleh tim CI. Setelah diproduksi, persetujuan terkunci.
 */
export const DESIGN_APPROVAL_STATUSES: readonly OrderStatus[] = ['PAID', 'IN_REVIEW'];

export function designExtOf(fileName: string): DesignFileExt | null {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return ext in DESIGN_FILE_TYPES ? (ext as DesignFileExt) : null;
}

const fileNameSchema = z
  .string()
  .trim()
  .min(3)
  .max(200)
  .refine((name) => designExtOf(name) !== null, {
    message: 'Format berkas harus PDF, PNG, JPG, AI, atau CDR',
  });

export const designUploadTicketSchema = z.object({
  fileName: fileNameSchema,
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(DESIGN_MAX_BYTES, { message: 'Ukuran berkas maksimal 50 MB' }),
});

/** Melampirkan berkas yang sudah selesai diunggah ke satu item. */
export const attachDesignSchema = z.object({
  key: z.string().min(10).max(300),
  fileName: fileNameSchema,
});

// ------------------------------------------------------------
// Design-first (KEPUTUSAN 2A.6): Studio membuat desain dan mengembalikan designId,
// keranjang dan pesanan hanya melampirkan designId.
// ------------------------------------------------------------

/**
 * Produk dan varian tujuan desain. Sama dengan isian item keranjang, supaya
 * ukuran kanvas dan jumlah sisi dihitung dari varian yang sama, dan saat
 * dilampirkan diperiksa cocok dengan item.
 */

/** Membuat desain dari berkas unggahan (sudah di-PUT ke tiket unggah). */
export const createUploadDesignSchema = attachDesignSchema.extend(designTargetShape);

/** Melampirkan desain yang sudah dibuat ke item keranjang atau pesanan. */
export const attachDesignByIdSchema = z.strictObject({ designId: idSchema });

/**
 * Meminta pratinjau berkas yang baru diunggah. Token diterbitkan bersama tiket
 * unggah, jadi hanya pengunggah yang bisa meminta pratinjau berkasnya.
 */
export const designPreviewRequestSchema = z.object({
  key: z.string().min(10).max(300),
  token: z.string().min(20).max(200),
});

/** Catatan opsional saat staf menyetujui atau membatalkan persetujuan desain. */
export const designApprovalNoteSchema = z
  .object({ note: z.string().trim().max(300).optional() })
  .default({});

export type DesignApprovalNote = z.infer<typeof designApprovalNoteSchema>;

export const orderItemParamsSchema = z.object({
  orderNumber: z.string().min(4).max(40),
  itemId: z.string().min(1).max(64),
});

export type DesignUploadTicketInput = z.infer<typeof designUploadTicketSchema>;
export type AttachDesignInput = z.infer<typeof attachDesignSchema>;
export type CreateUploadDesignInput = z.infer<typeof createUploadDesignSchema>;
export type AttachDesignByIdInput = z.infer<typeof attachDesignByIdSchema>;
export type DesignPreviewRequest = z.infer<typeof designPreviewRequestSchema>;
export type OrderItemParams = z.infer<typeof orderItemParamsSchema>;
